/**
 * Persistance diagnostic public → préremplissage FormApat après connexion.
 */
import {
  emptyChargesAnswers,
  loadDiagnosticResult,
  normalizePriorites,
  prioriteLabels,
  STATUTS,
} from '@/utils/diagnostic';

export const FORMAPAT_PREFILL_KEY = 'patrimonea_formapat_prefill_v1';
export const DIAGNOSTIC_JOURNEY_KEY = 'patrimonea_diagnostic_journey_v1';
const SERVER_PREFILL_CHECKED_KEY = 'patrimonea_formapat_server_prefill_checked_v1';
const PREFILL_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v) {
  return v == null ? '' : String(v).trim();
}

/**
 * Convertit une date saisie (texte ou ISO) en YYYY-MM-DD pour <input type="date">.
 */
export function normalizeDateToIso(raw) {
  const s = str(raw);
  if (!s) return '';
  const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const dmy = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, '0');
    const mm = dmy[2].padStart(2, '0');
    return `${dmy[3]}-${mm}-${dd}`;
  }
  const compact = s.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (compact) return `${compact[3]}-${compact[2]}-${compact[1]}`;
  return '';
}

/** Champ FormApat considéré comme non renseigné (y compris 0 issu d’un wipe API). */
export function isFormApatFieldEmpty(current) {
  if (current === null || current === undefined || current === '') return true;
  if (typeof current === 'number' && (!Number.isFinite(current) || current === 0)) return true;
  if (typeof current === 'string') {
    const t = current.trim();
    if (t === '' || t === '0') return true;
  }
  return false;
}

function statutLabel(value) {
  const found = STATUTS.find((s) => s.value === value);
  return found ? found.label : '';
}

/**
 * Transforme le résultat diagnostic (session) en snapshot compatible FormApat.persistDraft.
 */
export function mapDiagnosticToFormApatSnapshot(result) {
  const answers = result?.answers || {};
  const charges = { ...emptyChargesAnswers(), ...(answers.charges || {}) };
  const selected = normalizePriorites(answers);
  const labels = prioriteLabels(selected, answers.prioriteAutre);
  const objectif = labels.length
    ? labels.map((l, i) => `${i + 1}. ${l}`).join('\n')
    : '';

  return {
    first_name: str(answers.prenom),
    last_name: str(answers.nom),
    email: str(answers.email),
    telMobile: str(answers.telephone),
    fonction: str(charges.fonction) || statutLabel(answers.statut),
    adresse: str(charges.adresse),
    born: normalizeDateToIso(charges.date_born),
    comborn: str(charges.commune_born),
    depborn: str(charges.departement_born),
    salaire: numOrNull(charges.salaire),
    objectif,
    // Charges (clés alignées FormApat)
    loyer: numOrNull(charges.loyer),
    telephone: numOrNull(charges.telephone),
    electricite: numOrNull(charges.electricite),
    gaz: numOrNull(charges.gaz),
    eau: numOrNull(charges.eau),
    abonnement: numOrNull(charges.abonnement),
    ordures: numOrNull(charges.ordures),
    reparation: numOrNull(charges.reparation),
    fournitures: numOrNull(charges.fournitures),
    salaire_personnel_maison: numOrNull(charges.salaire_personnel_maison),
    popote: numOrNull(charges.popote),
    loyer_autre: numOrNull(charges.loyer_autre),
    pret_voiture: numOrNull(charges.pret_voiture),
    transport_public: numOrNull(charges.transport_public),
    assurance_voiture: numOrNull(charges.assurance_voiture),
    carburant: numOrNull(charges.carburant),
    entretien_transport: numOrNull(charges.entretien_transport),
    autres_transport: numOrNull(charges.autres_transport),
    habitat: numOrNull(charges.habitat),
    sante: numOrNull(charges.sante),
    autresAssurance: numOrNull(charges.autresAssurance),
    pret_personnel: numOrNull(charges.pret_personnel),
    pret_etudiant: numOrNull(charges.pret_etudiant),
    epargne: numOrNull(charges.epargne),
    // Contexte diagnostic (non bloquant)
    _diagnosticMeta: {
      ageRange: answers.ageRange || '',
      revenu: answers.revenu || '',
      actifs: answers.actifs || '',
      priorites: selected,
      statut: answers.statut || '',
      email: str(answers.email),
    },
  };
}

export function persistFormApatPrefillFromDiagnostic(result = null) {
  const src = result || loadDiagnosticResult();
  if (!src?.answers) return null;
  const snapshot = mapDiagnosticToFormApatSnapshot(src);
  try {
    localStorage.setItem(
      FORMAPAT_PREFILL_KEY,
      JSON.stringify({ ...snapshot, ts: Date.now() }),
    );
  } catch (_) {
    /* ignore */
  }
  markDiagnosticJourney(snapshot.email);
  return snapshot;
}

export function loadFormApatPrefill() {
  try {
    const raw = localStorage.getItem(FORMAPAT_PREFILL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearFormApatPrefill() {
  try {
    localStorage.removeItem(FORMAPAT_PREFILL_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function normalizeEmail(v) {
  return str(v).toLowerCase();
}

export function emailsMatch(a, b) {
  const ea = normalizeEmail(a);
  const eb = normalizeEmail(b);
  return Boolean(ea && eb && ea === eb);
}

export function markDiagnosticJourney(email) {
  const payload = { email: normalizeEmail(email), ts: Date.now() };
  try {
    sessionStorage.setItem(DIAGNOSTIC_JOURNEY_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore */
  }
}

export function hasActiveDiagnosticJourney() {
  try {
    const raw = sessionStorage.getItem(DIAGNOSTIC_JOURNEY_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    if (parsed.ts && Date.now() - Number(parsed.ts) > PREFILL_MAX_AGE_MS) return false;
    return true;
  } catch (_) {
    return false;
  }
}

export function clearDiagnosticJourney() {
  try {
    sessionStorage.removeItem(DIAGNOSTIC_JOURNEY_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function consumeFormApatPrefill() {
  clearFormApatPrefill();
  clearDiagnosticJourney();
}

function isPrefillUsable(prefill) {
  if (!prefill || typeof prefill !== 'object') return false;
  if (prefill.ts && Date.now() - Number(prefill.ts) > PREFILL_MAX_AGE_MS) return false;
  return Boolean(normalizeEmail(prefill.email || prefill._diagnosticMeta?.email));
}

function readPrefillSnapshot() {
  let prefill = loadFormApatPrefill();
  if (isPrefillUsable(prefill)) return prefill;
  const result = loadDiagnosticResult();
  if (result?.answers) return mapDiagnosticToFormApatSnapshot(result);
  return null;
}

/**
 * Inscription : préremplir seulement si le visiteur vient du tunnel diagnostic
 * (`?from=diagnostic` ou drapeau session), pas un leftover d’un autre parcours.
 */
export function loadRegisterPrefillFromDiagnostic(routeQuery = {}) {
  const fromQuery = String(routeQuery?.from || '').toLowerCase() === 'diagnostic';
  if (!fromQuery && !hasActiveDiagnosticJourney()) return null;
  const prefill = readPrefillSnapshot();
  if (!isPrefillUsable(prefill)) return null;
  return {
    first_name: str(prefill.first_name),
    last_name: str(prefill.last_name),
    email: str(prefill.email),
    tel: str(prefill.telMobile),
    adresse: str(prefill.adresse),
    fonction: str(prefill.fonction),
  };
}

/**
 * FormApat : n’injecter le diagnostic que si l’email du compte correspond.
 * Un compte créé sans diagnostic (ou un autre email) n’hérite pas du leftover.
 */
export function resolveFormApatPrefillForUser(userEmail) {
  if (!normalizeEmail(userEmail)) return null;
  const prefill = readPrefillSnapshot();
  if (!isPrefillUsable(prefill)) return null;
  const prefillEmail = prefill.email || prefill._diagnosticMeta?.email;
  if (!emailsMatch(prefillEmail, userEmail)) return null;
  return prefill;
}

/**
 * Applique le préremplissage uniquement sur les champs vides / nuls / 0
 * pour ne pas écraser un brouillon serveur déjà renseigné, tout en
 * récupérant les valeurs perdues après un wipe API (`|| 0`).
 */
export function applyFormApatPrefill(vm, prefill) {
  if (!vm || !prefill || typeof prefill !== 'object') return false;
  let applied = false;
  const skip = new Set(['ts', '_diagnosticMeta']);
  Object.keys(prefill).forEach((key) => {
    if (skip.has(key)) return;
    if (!(key in vm)) return;
    const incoming = prefill[key];
    if (incoming === null || incoming === undefined || incoming === '') return;
    if (typeof incoming === 'number' && incoming === 0) return;
    if (isFormApatFieldEmpty(vm[key])) {
      vm[key] = key === 'born' ? (normalizeDateToIso(incoming) || incoming) : incoming;
      applied = true;
    }
  });
  return applied;
}

/**
 * Évite d'interroger /api/diagnostic-prefill/ à chaque ouverture de FormApat :
 * une fois vérifié pour cet email sur cet appareil (trouvé ou non), on ne
 * réinterroge le serveur qu'après expiration (même fenêtre que le prefill lui-même).
 */
export function wasServerDiagnosticPrefillChecked(email) {
  const target = normalizeEmail(email);
  if (!target) return true;
  try {
    const raw = localStorage.getItem(SERVER_PREFILL_CHECKED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    if (normalizeEmail(parsed.email) !== target) return false;
    if (!parsed.ts || Date.now() - Number(parsed.ts) > PREFILL_MAX_AGE_MS) return false;
    return true;
  } catch (_) {
    return false;
  }
}

export function markServerDiagnosticPrefillChecked(email) {
  const target = normalizeEmail(email);
  if (!target) return;
  try {
    localStorage.setItem(SERVER_PREFILL_CHECKED_KEY, JSON.stringify({ email: target, ts: Date.now() }));
  } catch (_) {
    /* ignore */
  }
}
